import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import type { AutocompleteOption } from '../../../types';

type AutocompleteKeyDownProps = {
    options: AutocompleteOption[];
    activeIndex: number;
    showDefaultOption: boolean;
    showDropdown: boolean;
    setSelected: (index: number) => void;
    clearHovered: () => void;
    setOptions: (opts: AutocompleteOption[]) => void;
    setNoResults: (val: boolean) => void;
    selectCurrentOption: () => void;
};

export const useAutocompleteKeyDown = ({
    options,
    activeIndex,
    showDefaultOption,
    showDropdown,
    setSelected,
    clearHovered,
    setOptions,
    setNoResults,
    selectCurrentOption,
}: AutocompleteKeyDownProps) => {
    const activeIndexRef = useRef(activeIndex);
    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    return useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (!showDropdown) {
                return;
            }

            const key = e.key;
            const total = options.length + (showDefaultOption ? 1 : 0);
            const current = activeIndexRef.current;
            const hasAny = total > 0;
            const hasSelection = current !== -1;

            switch (key) {
                case 'ArrowDown': {
                    if (!hasAny) return;
                    const next = (current + 1 + total) % total;
                    setSelected(next);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'ArrowUp': {
                    if (!hasAny) return;
                    const prev = (current - 1 + total) % total;
                    setSelected(prev);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'Home': {
                    if (!hasAny) return;
                    setSelected(0);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'End': {
                    if (!hasAny) return;
                    setSelected(total - 1);
                    clearHovered();
                    e.preventDefault();
                    return;
                }

                case 'Enter':
                case 'Tab': {
                    if (hasSelection) selectCurrentOption();
                    if (key === 'Enter') e.preventDefault();
                    return;
                }

                case 'Escape': {
                    setOptions([]);
                    setSelected(-1);
                    clearHovered();
                    setNoResults(false);
                    return;
                }

                default:
                    return;
            }
        },
        [showDropdown, options.length, showDefaultOption, setSelected, clearHovered, setOptions, setNoResults, selectCurrentOption],
    );
};
